import React from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Button, 
  Divider 
} from '@mui/material';
import { LockOutlined, ErrorOutline } from '@mui/icons-material';
import { AdminPermissionClaims } from '@otw/models/user';

interface PermissionDeniedProps {
  category: keyof AdminPermissionClaims;
  action: string;
  description?: string;
  contactEmail?: string;
  contactPerson?: string;
}

/**
 * Component to display when a user doesn't have the required permissions
 * for a specific action.
 */
export const PermissionDenied: React.FC<PermissionDeniedProps> = ({
  category,
  action,
  description,
  contactEmail = 'admin@optictruckworks.com',
  contactPerson = 'your administrator',
}) => {
  const formattedCategory = category.replace(/_/g, ' ');
  const formattedAction = action.replace(/_/g, ' ');
  
  const getActionVerb = () => {
    switch (action) {
      case 'view': return 'viewing';
      case 'create': return 'creating';
      case 'modify': return 'modifying';
      case 'delete': return 'deleting';
      case 'upload': return 'uploading';
      default: return action;
    }
  };

  return (
    <Box sx={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      padding: 3, 
      height: '100%' 
    }}>
      <Paper 
        elevation={3} 
        sx={{ 
          maxWidth: 600, 
          width: '100%', 
          padding: 4, 
          textAlign: 'center',
          borderRadius: 2,
          border: '1px solid #e0e0e0'
        }}
      >
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          marginBottom: 2 
        }}>
          <LockOutlined 
            color="error" 
            sx={{ 
              fontSize: 60, 
              marginBottom: 2 
            }} 
          />
        </Box>

        <Typography variant="h5" component="h1" gutterBottom color="error" fontWeight="bold">
          Permission Denied
        </Typography>

        <Divider sx={{ marginY: 2 }} />

        <Typography variant="body1" gutterBottom sx={{ marginBottom: 3 }}>
          You don't have permission for <strong>{getActionVerb()} {formattedCategory}</strong>.
        </Typography>

        {description && (
          <Typography variant="body2" color="text.secondary" paragraph>
            {description}
          </Typography>
        )}

        <Box sx={{ 
          backgroundColor: '#f8f9fa', 
          padding: 2, 
          borderRadius: 1, 
          marginY: 3 
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: 1 }}>
            <ErrorOutline fontSize="small" color="info" sx={{ marginRight: 1 }} />
            <Typography variant="subtitle2" color="text.secondary">
              What to do?
            </Typography>
          </Box>
          <Typography variant="body2" align="left">
            Please contact {contactPerson} to request <strong>"{action}"</strong> permission 
            for the <strong>"{formattedCategory}"</strong> section.
            {contactEmail && (
              <> You can reach out to <a href={`mailto:${contactEmail}`}>{contactEmail}</a>.</>
            )}
          </Typography>
        </Box>

        <Button 
          variant="outlined" 
          onClick={() => window.history.back()} 
          sx={{ marginTop: 2 }}
        >
          Go Back
        </Button>
      </Paper>
    </Box>
  );
};

export default PermissionDenied; 